import { useEffect, useMemo, useState } from 'react';
import C from 'classnames';
import { useTranslation } from 'react-i18next';
import { getTransferEndpoints } from 'services/connectService';

import s from './styles.module.scss';

import type { FC } from 'react';
import type {
  ITransferEndpoint,
  ITransferSnapshot,
} from 'services/connectService';

interface IProps {
  /** Non-null once a third party is on the contact. */
  transfer: ITransferSnapshot | null;
  patientName: string;
  onDial: (target: { endpointId?: string; rawNumber?: string }) => void;
  onSwap: () => void;
  onJoin: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onHide: () => void;
}

/**
 * Replaces the tile row, the same way the keypad does — the capacity readout
 * stays mounted above it, which matters more here than anywhere else in the
 * panel: it is the instrument that answers "is there anyone to transfer to?".
 *
 * Two faces. Before a third party is dialled it is a picker: type a number, or
 * choose a quick connect. Once someone is on the line it becomes the controls
 * for a call with three people in it.
 */
const Transfer: FC<IProps> = ({
  transfer,
  patientName,
  onDial,
  onSwap,
  onJoin,
  onComplete,
  onCancel,
  onHide,
}) => {
  const { t } = useTranslation();
  const [endpoints, setEndpoints] = useState<ITransferEndpoint[] | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    getTransferEndpoints().then((list) => {
      if (alive) setEndpoints(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  // The same field filters the quick connects and holds a number to dial, so an
  // agent who needs a number that is not in the list does not have to find a
  // second control to type it into.
  const typed = query.trim();
  // Dialable = phone characters only, in any human format — '(555) 123-4567'
  // included — with at least one character that survives the strip down to a
  // dialable string (addTransferConnection removes the formatting).
  const isDialable = /^[0-9+*#\- ()]+$/.test(typed) && /[0-9+*#]/.test(typed);

  const visible = useMemo(() => {
    if (!endpoints) return [];
    if (!typed) return endpoints;
    const needle = typed.toLowerCase();
    // Guarded on being non-empty: every string includes '', so an all-letters
    // query would otherwise match every endpoint through its phone number and
    // the name filter would never narrow the list.
    const digits = typed.replace(/\D/g, '');
    return endpoints.filter(
      (e) =>
        e.name.toLowerCase().includes(needle) ||
        (!!digits && e.phoneNumber.replace(/\D/g, '').includes(digits)),
    );
  }, [endpoints, typed]);

  if (transfer) {
    // The patient hanging up leaves only the agent and the third party on the
    // contact. Swap and Join then have no one to swap to or join, and Complete
    // would drop the agent and leave the third party alone on an empty call —
    // so all three go dark. Cancel stays: it is the way out, but dropping the
    // third party now drops the last other person on the call and Connect ends
    // it, which agents met as "Cancel ended my call". The hint says so first.
    const { customerLeft } = transfer;
    const bothPartiesOnCall = transfer.isConnected && !customerLeft;
    const customerState = (() => {
      if (customerLeft) return t('connectPanel.transfer.hungUp');
      if (transfer.customerOnHold) return t('connectPanel.transfer.onHold');
      return t('connectPanel.transfer.onTheLine');
    })();

    return (
      <>
        {/* Both legs, always. Swap is meaningless unless the agent can see
            which side they are currently talking to. */}
        <div className={s['transfer-parties']}>
          <div className={C(s['transfer-party'], s['transfer-party-customer'])}>
            <span className={s['transfer-party-name']}>{patientName}</span>
            <span
              className={s['transfer-party-state']}
              data-cy="transfer-customer-state"
            >
              {customerState}
            </span>
          </div>
          <div className={s['transfer-party']}>
            <span className={s['transfer-party-name']}>
              {transfer.name || t('connectPanel.transfer.unknownParty')}
            </span>
            <span className={s['transfer-party-state']}>
              {transfer.isConnected
                ? t('connectPanel.transfer.onTheLine')
                : t('connectPanel.transfer.ringing')}
            </span>
          </div>
        </div>

        <div className={s['transfer-actions']}>
          {/* Borrows the drawer's own helper-text style (the quick-connect
              list's loading/empty line). role="status" so a screen reader
              hears that the patient left, not just a sighted agent. */}
          {customerLeft && (
            <span
              className={s['transfer-empty']}
              role="status"
              data-cy="transfer-patient-left"
            >
              {t('connectPanel.transfer.patientLeftHint')}
            </span>
          )}
          <div className={s['transfer-actions-pair']}>
            <button
              type="button"
              className={s['transfer-action']}
              onClick={onSwap}
              disabled={!bothPartiesOnCall}
              data-cy="transfer-swap"
            >
              {t('connectPanel.transfer.swap')}
            </button>
            <button
              type="button"
              className={s['transfer-action']}
              onClick={onJoin}
              disabled={!bothPartiesOnCall}
              data-cy="transfer-join"
            >
              {t('connectPanel.transfer.join')}
            </button>
          </div>
          {/* The two ways out, each full width so neither is hit by accident. */}
          <button
            type="button"
            className={C(s['transfer-action'], s['transfer-action-primary'])}
            onClick={onComplete}
            disabled={!bothPartiesOnCall}
            data-cy="transfer-complete"
          >
            {t('connectPanel.transfer.complete')}
          </button>
          <button
            type="button"
            className={C(s['transfer-action'], s['transfer-action-cancel'])}
            onClick={onCancel}
            data-cy="transfer-cancel"
          >
            {t('connectPanel.transfer.cancel')}
          </button>
        </div>

        {/* No Hide button on this face: a live transfer pins the drawer open
            (isTransferOpen ORs on the transfer itself), so the button would sit
            there doing nothing. Complete or Cancel are the ways out. */}
      </>
    );
  }

  return (
    <>
      <div className={s['transfer-search-wrapper']}>
        <div className={s['transfer-search']}>
          <input
            className={s['transfer-input']}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('connectPanel.transfer.searchPlaceholder')}
            aria-label={t('connectPanel.transfer.searchPlaceholder')}
            data-cy="transfer-input"
          />
          <button
            type="button"
            className={s['transfer-dial']}
            onClick={() => onDial({ rawNumber: typed })}
            disabled={!isDialable}
            data-cy="transfer-dial"
          >
            {t('connectPanel.transfer.dial')}
          </button>
        </div>
      </div>

      <div className={s['transfer-list']} data-cy="transfer-list">
        {endpoints === null && (
          <span className={s['transfer-empty']}>
            {t('connectPanel.transfer.loading')}
          </span>
        )}
        {endpoints !== null && !visible.length && (
          <span className={s['transfer-empty']}>
            {t('connectPanel.transfer.empty')}
          </span>
        )}
        {visible.map((endpoint) => (
          <button
            key={endpoint.id}
            type="button"
            className={s['transfer-option']}
            onClick={() => onDial({ endpointId: endpoint.id })}
            data-cy="transfer-option"
          >
            <span className={s['transfer-option-name']}>{endpoint.name}</span>
            {!!endpoint.phoneNumber && (
              <span className={s['transfer-option-number']}>
                {endpoint.phoneNumber}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={s['transfer-hide-wrapper']}>
        <button type="button" className={s['keypad-hide']} onClick={onHide}>
          {t('connectPanel.transfer.hide')}
        </button>
      </div>
    </>
  );
};

export default Transfer;
